
import { Component, Vue, Prop, PropSync } from 'vue-property-decorator';
import BaseCard from "@/components/base/BaseCard.vue";
import { OfferSingle } from "@/services/offers/OfferSingle";
import EditIconBtn from "@/components/base/buttons/EditIconBtn.vue";
import BaseAutocomplete from "@/components/base/design/BaseAutocomplete.vue";
import { getEmployeesOffersV2, getOfferTrackingDomainsList, getOfferTrackingList } from "@/api/offers";
import { IEditableOfferFields, IOffer, ITracking, ITrackingDomain } from "@/api/types/offers";
import { getNumEnding } from "@/utils";
import BaseSelect from "@/components/base/design/BaseSelect.vue";
import { translateBoolean, translateOffersStatus } from "@/utils/translate";
import BaseQuestionMarkTooltip from "@/components-UI/BaseQuestionMarkTooltip.vue";
import CookiesSelect from "@/components/base/form/CookiesSelect.vue";
import cookiesSelectItems from "@/mappings/cookies-select-items";
import MaskedTextField from "@/components/base/form/MaskedTextField.vue";
import { namespace } from "vuex-class";
import { useOfferDetailStore } from "@/store_v3/offerCard";
import { canEditOffers } from "@/utils/checkRole";
import { getChangedFields, unbindAnObject } from "@/utils";
import BaseTooltip from "@/components-UI/BaseTooltip.vue";

const offers = namespace('offersModule');

interface ITrackingOptions {
    type: string;
    id: string;
    supId?: string;
    supValue?: string;
    items?: any[];
    itemText?: string;
    itemValue?: string;
    api?: () => Promise<ITracking[] | ITrackingDomain[]>;
    key?: number;
    tooltip?: string
    trueValue?: boolean | string;
    falseValue?: boolean | string;
}

@Component({
    components: {
        BaseTooltip,
        MaskedTextField,
        CookiesSelect,
        QuestionMarkTooltip: BaseQuestionMarkTooltip,
        BaseSelect,
        BaseAutocomplete,
        EditIconBtn, BaseCard,
    },
})
export default class OfferCardTracking extends Vue {
    get canEditOffer(): boolean {
        return canEditOffers();
    }

    @Prop({ required: true }) offer!: OfferSingle;
    @PropSync('isLoading') isLoadingSync!: boolean;
    @offers.Action('GET_SELECTS_ITEMS') getSelectItems;
    @offers.State('selects_items') selectsItems!: { [key: string]: any[] | undefined };

    isEdit = false;
    offerData: IEditableOfferFields = {} as IEditableOfferFields;
    redirect: IOffer | null = null;
    redirectUrl: number | null = null;
    translateBoolean = translateBoolean;
    cookiesSelectItems = cookiesSelectItems;
    cookiesValue = 0;
    userCookiesValue = 0;
    store = useOfferDetailStore();
    defaultData = {} as IEditableOfferFields;

    trackingOptions: ITrackingOptions[] = [
        {
            type: 'select',
            id: 'tracking_protocol',
            supId: 'type',
            supValue: 'name',
            items: [],
            itemText: 'name',
            itemValue: 'type',
            api: getOfferTrackingList,
            key: 0,
        },
        { type: 'redirect', id: 'redirect_offer_id' },
        { type: 'redirect_url', id: 'redirect_url_id' },
        { type: 'session', id: 'session_hours' },
        { type: 'hours', id: '' },
        { type: 'switch', id: 'approve_conversions' },
        { type: 'switch', id: 'multiple_conversions' },
        {
            type: 'select',
            id: 'postbacks_when_inactive',
            items: [
                { text: 'Всегда', value: true },
                { text: 'Когда активен', value: false },
            ],
            itemText: 'text',
            itemValue: 'value',
            key: 0,
        },
        {
            type: 'select',
            id: 'tracking_domain',
            supId: 'id',
            supValue: 'domain',
            items: [],
            itemText: 'domain',
            itemValue: 'id',
            api: getOfferTrackingDomainsList,
            key: 0,
        },
        { type: 'switch', id: 'deep_links_enabled' },
        { type: 'switch', trueValue: 'allow', falseValue: 'reject', id: 'non_unique_click_attribution' },
        { type: 'switch', trueValue: 'disabled', falseValue: 'reject', id: 'proactive_click_fraud_prevention' },
        { type: 'switch', id: 'enforce_secure_tracking_link' },
        { type: 'switch', id: 'hide_referer' },
    ];

    get getTrackingOptions(): ITrackingOptions[] {
        let options = this.trackingOptions;
        const isNoRedirect = this.isEdit ? !this.redirect : !this.offer.redirect_offer_id;
        isNoRedirect && (options = options.filter(i => i.id !== 'redirect_url_id'));
        return this.isEdit && this.cookiesValue === 0 ? options : options.filter(i => i.type !== 'hours');
    }

    get getCookiesDefaultValues(): { [key: number]: string } {
        return Object.fromEntries(this.cookiesSelectItems.map(i => ([i.id, i.text])));
    }

    get getSessionHoursValue(): string {
        const ends = ['час', 'часа', 'часов'];
        const v = this.offer.session_hours;
        return this.getCookiesDefaultValues[v] || `${v} ${getNumEnding(v, ends)}`;
    }

    get getRedirectData(): any {
        return {
            errorText: 'Оффер не найден',
            apiMethod: getEmployeesOffersV2,
            key: 'search',
            itemValue: 'legacy_id',
            clearable: true,
            template: (i: IOffer) => i.legacy_id + ' ' + i.name + this.getRedirectOfferStatus(i),
            placeholder: 'Выберите оффер',
        };
    }

    translateValueForRead(item: ITrackingOptions): number | string | boolean {
        const targetProp = this.offer[item.id];
        const value = item.supValue && targetProp ? targetProp[item.supValue] : targetProp;

        if (item.id === 'postbacks_when_inactive') {
            return value ? 'Всегда' : 'Когда активен';
        }

        if (['select', 'redirect', 'session', 'hours'].includes(item.type)) {
            return value;
        }

        if (typeof value === 'string') {
            return translateBoolean(value === 'disabled' || value === 'allow');
        }

        if (item.id === 'approve_conversions') {
            return translateBoolean(!value);
        }

        return translateBoolean(value);
    }

    setValues(): void {
        const options = this.trackingOptions.filter(opt => !['redirect', 'hours', 'session', 'redirect_url'].includes(opt.type));
        const booleanValues = ['multiple_conversions', 'deep_links_enabled', 'hide_referer'];
        options.forEach(opt => {
            const value = this.offer[opt.id] && opt.supId ? this.offer[opt.id][opt.supId] : this.offer[opt.id];
            this.offerData[opt.id] = booleanValues.includes(opt.id) ? !!value : opt.id === 'approve_conversions' ? !value : value;
        });
        this.selectCookies(this.offer.session_hours!);
    }

    editInfo(isEdit: boolean): void {
        if (isEdit) {
            this.getItems();
            this.setValues();
            this.defaultData = unbindAnObject(this.offerData);
        } else {
            this.updateOffer();
        }
        this.isEdit = isEdit;
        if (!isEdit) {
            // setTimeout(() => this.setRedirectWidth(), 1000);
        }
    }

    getRedirectOfferStatus(item: IOffer): string {
        return item.status ? ' (' + translateOffersStatus(item.status).text + ')' : '';
    }

    getRedirectOffer(item: IOffer): void {
        this.redirect = item;
        if (this.offer.redirect_offer_id === item.legacy_id) {
            this.redirectUrl = this.offer.redirect_url_id!;
        } else {
            this.redirectUrl = null;
        }
    }

    get getAdditionalUrl(): { id: number | null, text: string }[] {
        const urls = this.redirect?.additional_urls?.filter(i => i.status !== 'deleted');
        return urls?.length ? urls.map(i => ({ id: i.legacy_id!, text: i.name! })) : [];
    }

    get isDisableRedirectUrl(): boolean {
        return this.getAdditionalUrl.length === 0;
    }

    get getRedirectUrlItems(): { id: number | null, text: string }[] {
        const def = { id: null, text: "По умолчанию" };
        return [def, ...this.getAdditionalUrl];
    }

    selectRedirectUrl(value: number | null): void {
        this.redirectUrl = value;
    }

    selectCookies(value: number): void {
        const isDefValue = this.getCookiesDefaultValues[value];
        if (isDefValue && value !== 0) {
            this.offerData.session_hours = value;
            this.cookiesValue = value;
        } else {
            this.cookiesValue = 0;
            this.userCookiesValue = value;
            this.offerData.session_hours = value;
        }
    }

    selectUserCookies(value: number): void {
        const val = value > 17119 ? 17119 : value;
        this.userCookiesValue = val;
        this.offerData.session_hours = val;
    }

    async getItems(): Promise<void> {
        try {
            this.isLoadingSync = true;
            const selects = this.trackingOptions.filter(opt => opt.type === 'select' && !!opt.api);
            if (selects.every(s => s.items && s.items.length > 0)) return;

            const requestParams = {};
            const setItems = () => {
                selects.forEach(select => {
                    const items = this.selectsItems[select.id] || [];
                    if (items.length === 0) {
                        requestParams[select.id] = { property: select.id, api: select.api };
                    } else {
                        select.items = items;
                    }
                });
            };
            setItems();
            if (Object.values(requestParams).length === 0) return;
            await this.getSelectItems(Object.values(requestParams));
            setItems();
        } finally {
            this.isLoadingSync = false;
        }
    }

    get isRedirectChanged(): boolean {
        const { redirect_offer_id: id, redirect_url_id: urlId } = this.offer;
        const isUrlChanged = this.redirectUrl !== urlId;
        if (isUrlChanged) return true;
        return (this.redirect !== null || id !== null) && this.redirect?.legacy_id !== id;
    }

    async updateOffer(): Promise<void> {
        if (typeof (this.offerData.tracking_domain as string | number) === 'string' && !this.offerData.tracking_domain) {
            this.offerData.tracking_domain = null as any;
        }

        this.isLoadingSync = true;
        const redirectValue = this.redirect?.id ? this.redirect?.id : null;
        const isUpdateRedirect = this.isRedirectChanged ?
            await this.offer.setRedirect(redirectValue, this.redirectUrl!) : false;
        const isUpdate = await this.offer.editOffer(getChangedFields(this.defaultData, this.offerData));
        if (isUpdateRedirect || isUpdate) this.store.setIsUpdateOffer(true);
        this.$emit('update-offer');
        this.isLoadingSync = false;
    }

    mounted(): void {
        // window.addEventListener('resize', this.setRedirectWidth);
        // setTimeout(() => this.setRedirectWidth(), 500);
    }

    beforeDestroy(): void {
        // window.removeEventListener('resize', this.setRedirectWidth);
    }

    setRedirectWidth(): void {
        const redirectWrap = document.querySelector('.offer-card-tracking__redirect-wrap') as HTMLElement;
        const redirectLink = redirectWrap.querySelector('.offer-card-tracking__redirect-link') as HTMLElement;
        const textWidth = (this.$vuetify.breakpoint.width <= 576 ? 175 : 250) + (this.$vuetify.breakpoint.width > 1240 ? 60 : 20);
        redirectWrap.style.width = `${this.$el.clientWidth - textWidth}px`;
        if (redirectLink) {
            redirectLink.style.width = redirectWrap.clientWidth > redirectLink.clientWidth ? 'fit-content' : 'inherit';
        }
    }
}
